/**
 * File as the HR Portal (28 Oct, Rusty): on the demo page, a New user or a
 * Leaver filed in ITSM exactly as Timo's HR Portal files it (Source "HR
 * Portal", a 32-character External Ticket, the demo HR person as Customer,
 * the demo manager on the form), so the HelpDesk picks it up as it will a
 * real one. "Pick it up now" skips the watcher's wait. UAT only: the server
 * has no such routes in production.
 */

import { Alert, Button, Checkbox, Field, Input, Typography } from "@norican/react";
import { useState } from "react";
import { Link } from "react-router";

import { ErrorState, SectionCard } from "@/components/common";
import { EmployeePicker } from "@/features/tickets";
import { useT } from "@/i18n";
import { type HrPortalFiled, useMe } from "@/lib/api";
import { itsmTicketUrl } from "@/lib/ticket-link";

import { useHrPortalFile, useHrPortalPickUp } from "./api";

/** `YYYY-MM-DD`, `days` from today (local), as a date input holds it. */
function daysAhead(days: number): string {
  const day = new Date();
  day.setDate(day.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
}

function Result({ filed }: { filed: HrPortalFiled }) {
  const { t } = useT();
  const { data: me } = useMe();
  const pickUp = useHrPortalPickUp();
  const [caseUrl, setCaseUrl] = useState<string | null>(filed.case_url_path ?? null);
  if (!filed.ok) {
    return <Alert tone="warning">{filed.detail}</Alert>;
  }
  const itsm = itsmTicketUrl(me?.itsm_url, "SERVICE_REQUEST", filed.number);
  return (
    <Alert tone="success">
      <div className="flex flex-col gap-2">
        <span>
          {t("demoPersona.hrPortal.filed", { number: filed.number ?? "?" })}{" "}
          <span className="text-fg-muted text-sm">
            {t("demoPersona.hrPortal.external", { id: filed.external_ticket ?? "" })}
          </span>
        </span>
        <div className="flex flex-wrap items-center gap-3">
          {itsm ? (
            <a href={itsm} target="_blank" rel="noreferrer" className="underline">
              {t("demoPersona.hrPortal.openItsm")}
            </a>
          ) : null}
          {caseUrl ? (
            <Link to={caseUrl} className="underline">
              {t("demoPersona.hrPortal.openCase")}
            </Link>
          ) : (
            <Button
              size="sm"
              variant="outline"
              tone="neutral"
              loading={pickUp.isPending}
              disabled={!filed.ticket_id}
              onClick={() =>
                filed.ticket_id &&
                pickUp.mutate(
                  { ticketId: filed.ticket_id, kind: filed.kind },
                  { onSuccess: (done) => setCaseUrl(done.case_url_path ?? null) },
                )
              }
            >
              {t("demoPersona.hrPortal.pickUp")}
            </Button>
          )}
        </div>
        {caseUrl ? null : (
          <Typography variant="caption" tone="muted">
            {pickUp.data && !pickUp.data.ok ? pickUp.data.detail : t("demoPersona.hrPortal.wait")}
          </Typography>
        )}
        {pickUp.isError ? <ErrorState variant="inline" error={pickUp.error} /> : null}
      </div>
    </Alert>
  );
}

export function HrPortalCard() {
  const { t } = useT();
  const file = useHrPortalFile();
  const [name, setName] = useState("");
  const [site, setSite] = useState("");
  const [start, setStart] = useState(() => daysAhead(14));
  const [systems, setSystems] = useState(false);
  const [refId, setRefId] = useState<string | null>(null);
  const [refName, setRefName] = useState<string | null>(null);
  const [leaverId, setLeaverId] = useState<string | null>(null);
  const [leaverName, setLeaverName] = useState<string | null>(null);
  const [lastDay, setLastDay] = useState(() => daysAhead(14));
  const [results, setResults] = useState<HrPortalFiled[]>([]);
  const keep = (done: HrPortalFiled) => setResults((all) => [done, ...all].slice(0, 4));

  const fileNew = () =>
    file.mutate(
      {
        kind: "new",
        body: {
          name: name.trim() || null,
          site: site.trim() || null,
          start_date: start || null,
          systems_needed: systems,
          reference_id: systems ? refId : null,
        },
      },
      { onSuccess: keep },
    );
  const fileLeaver = () =>
    file.mutate(
      { kind: "leave", body: { person_id: leaverId, last_day: lastDay || null } },
      { onSuccess: keep },
    );

  return (
    <SectionCard title={t("demoPersona.hrPortal.title")}>
      <div className="flex flex-col gap-4">
        <Typography variant="body-sm" tone="muted">
          {t("demoPersona.hrPortal.intro")}
        </Typography>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="flex flex-col gap-3">
            <Typography variant="body-sm" className="font-semibold">
              {t("demoPersona.hrPortal.newUser")}
            </Typography>
            <Field name="hr-portal-name" label={t("demoPersona.hrPortal.name")}>
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="[UAT TEST] Demo New Hire"
                maxLength={120}
              />
            </Field>
            <Field name="hr-portal-site" label={t("demoPersona.hrPortal.site")}>
              <Input
                value={site}
                onChange={(event) => setSite(event.target.value)}
                placeholder="US - USLAG"
                maxLength={80}
              />
            </Field>
            <Field name="hr-portal-start" label={t("demoPersona.hrPortal.startDate")}>
              <Input type="date" value={start} onChange={(event) => setStart(event.target.value)} />
            </Field>
            <Checkbox.Root
              checked={systems}
              onCheckedChange={(next) => setSystems(next === true)}
              label={t("demoPersona.hrPortal.systems")}
            />
            {systems ? (
              <Field name="hr-portal-reference" label={t("demoPersona.hrPortal.reference")}>
                <EmployeePicker
                  name="hr-portal-reference"
                  value={refId}
                  defaultDisplayValue={refName}
                  onChange={(entry) => {
                    setRefId(entry?.id ?? null);
                    setRefName(entry?.display_name ?? null);
                  }}
                  aria-label={t("demoPersona.hrPortal.reference")}
                />
              </Field>
            ) : null}
            <div>
              <Button size="sm" loading={file.isPending} onClick={fileNew}>
                {t("demoPersona.hrPortal.fileNew")}
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <Typography variant="body-sm" className="font-semibold">
              {t("demoPersona.hrPortal.leaver")}
            </Typography>
            <Field name="hr-portal-leaver" label={t("demoPersona.hrPortal.leaving")}>
              <EmployeePicker
                name="hr-portal-leaver"
                value={leaverId}
                defaultDisplayValue={leaverName}
                onChange={(entry) => {
                  setLeaverId(entry?.id ?? null);
                  setLeaverName(entry?.display_name ?? null);
                }}
                aria-label={t("demoPersona.hrPortal.leaving")}
              />
            </Field>
            <Field name="hr-portal-last-day" label={t("demoPersona.hrPortal.lastDay")}>
              <Input
                type="date"
                value={lastDay}
                onChange={(event) => setLastDay(event.target.value)}
              />
            </Field>
            <div>
              <Button size="sm" loading={file.isPending} onClick={fileLeaver}>
                {t("demoPersona.hrPortal.fileLeaver")}
              </Button>
            </div>
          </div>
        </div>
        {file.isError ? <ErrorState variant="inline" error={file.error} /> : null}
        {results.map((filed) => (
          <Result key={filed.external_ticket ?? filed.detail} filed={filed} />
        ))}
      </div>
    </SectionCard>
  );
}
